import './ProductExtraBrowseTool.less';
import React, {useState, useEffect, useRef} from 'react';
import Request from '../../../api/Request';
import PagingPanel from '../../../components/paging-panel/PagingPanel';
import Spinner from '../../../components/spinner/Spinner';
import __ from '../../../i18n';
import ProductFilter from '../../widgets/product-filter/ProductFilter';
import ProductAttributeRelationsLoader from '../../singleton/ProductAttributeRelationsLoader';
import Icon from '../../../components/icon/Icon';
import getFilterableAttributeGroupsByProductClasses from '../../utils/product-attribute/getFilterableAttributeGroupsByProductClasses';
import getFilterableAttributes from "../../utils/product-attribute/getFilterableAttributes";
import parseGroupedAttributeIds from "../../utils/product-attribute/parseGroupedAttributeIds";
import ProductImageView from "../../widgets/product-image-view/ProductImageView";

export default function ProductExtraBrowseTool({
    title,
    productClassType,
    ProductExtraListParcel,
    defaultValue = null,
    onChange = value => console.log('(ProductExtraBrowseTool) onChange is omitted: ', value),
    onCancel = () => {},
}) {

    const pageSize = 24;

    const [data, setData] = useState({items: [], total: 0, relations: {}});

    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState(null);

    const [page, setPage] = useState(0);

    const [keyword, setKeyword] = useState('');

    const [groupedAttributeIds, setGroupedAttributeIds] = useState({});

    const [priceRange, setPriceRange] = useState([null, null]);

    const [orders, setOrders] = useState([['priority', false], ['publish_time', false]]);

    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        if (value !== defaultValue) {
            onChange(value, data.relations.image);
        }
    }, [value]);

    const getParcel = () => {
        const {
            brandIds,
            conditionLevels,
            productAttributeIdLists
        } = parseGroupedAttributeIds(groupedAttributeIds);

        return new ProductExtraListParcel(
            {
                keyword,
                brandIds,
                conditionLevels,
                productAttributeIdLists,
                priceRange,
            },
            orders,
            page,
            pageSize
        );
    };

    useEffect(() => {
        setIsLoading(true);
        setError(null);

        Request.add(getParcel())
            .then(data => {
                setData(data);
            })
            .catch(error => {
                setError(error.getMessage());
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [keyword, page, groupedAttributeIds, priceRange]);

    let setKeywordTimer = null;

    const setKeywordDelay = (newKeyword) => {
        if (setKeywordTimer !== null) {
            clearTimeout(setKeywordTimer);
        }

        setKeywordTimer = setTimeout(() => setKeyword(newKeyword), 300);
    };

    const getIsSelected = (item) => {
        return value !== null && value.id === item.id;
    };

    const [attributeRelations, setAttributeRelations] = useState(ProductAttributeRelationsLoader.getData());

    useEffect(() => {
        if (attributeRelations === null) {
            const listener = () => {
                setAttributeRelations(ProductAttributeRelationsLoader.getData());
            };
            ProductAttributeRelationsLoader.addListener(listener);
            ProductAttributeRelationsLoader.load();

            return () => ProductAttributeRelationsLoader.removeListener(listener);
        }
    }, []);

    const getFilterableAttributeGroups = () => {
        const productClasses = attributeRelations.product_class
            .filter(t => t.type === productClassType);

        return getFilterableAttributeGroupsByProductClasses(
            productClasses.map(t => t.id),
            attributeRelations
        );
    };

    const __getFilterableAttributes = () => {
        return getFilterableAttributes(attributeRelations);
    };

    const renderProductFilter = () => {
        if (attributeRelations === null) {
            return null;
        }

        return <ProductFilter
            attributeGroups={getFilterableAttributeGroups()}
            attributes={__getFilterableAttributes()}
            defaultGroupedAttributeIds={groupedAttributeIds}
            defaultPriceRange={priceRange}
            onGroupedAttributeIdsChange={setGroupedAttributeIds}
            onPriceRangeChange={setPriceRange}
        />;
    };

    const inputRef = useRef(null);

    return <div className="product-extra-browse-tool">
        <div className="main-col">
            <div className="heading">
                <div className="left">
                    <div className="title">
                        {title}
                    </div>
                    {
                        renderProductFilter()
                    }
                </div>

                <div className="right">
                    {
                        error !== null &&
                        <div className="error">
                            {error}
                        </div>
                    }

                    {
                        isLoading &&
                        <Spinner/>
                    }

                    <div className="search-box">
                        <input
                            type="text"
                            onKeyUp={ev => setKeywordDelay(ev.target.value)}
                            onChange={ev => setKeywordDelay(ev.target.value)}
                            onPaste={ev => setKeywordDelay(ev.target.value)}
                            placeholder={__('Search')}
                            ref={inputRef}
                        />

                        <button
                            type="button"
                            onClick={() => inputRef.current.focus()}
                        >
                            <Icon name="search"/>
                        </button>
                    </div>

                    <button
                        type="button"
                        className="close-button"
                        onClick={() => {
                            onCancel();
                        }}
                    >
                        <Icon name="times"/>
                    </button>
                </div>
            </div>
            <div className="body">
                <div className="content-area">
                    {
                        data.items.length > 0 &&
                        <div className="grid-view">
                            <ul>
                                {
                                    data.items.map(item => {
                                        const selected = getIsSelected(item);

                                        return <li
                                            key={item.id}
                                            className={
                                                (selected ? ' selected' : '')
                                            }
                                            onClick={() => setValue(item)}
                                        >
                                            <div className="image-container">
                                                <ProductImageView
                                                    avatarImage={data.relations.image.find(im => im.id === item.avatar_image_id)}
                                                    hoverImage={data.relations.image.find(im => im.id === item.hover_image_id)}
                                                />
                                            </div>
                                            <div className="name">
                                                {item.name}
                                            </div>
                                        </li>;
                                    })
                                }
                            </ul>
                        </div>
                    }
                    {
                        !isLoading &&
                        error === null &&
                        data.items.length === 0 &&
                        <div className="empty-notify">
                            {__('No products found')}
                        </div>
                    }
                </div>
                <div className="bottom-area">
                    <PagingPanel
                        currentPage={page}
                        pageSize={pageSize}
                        totalItems={data.total}
                        goTo={setPage}
                    />
                </div>
            </div>
        </div>
        <div className="left-col">
            {
                renderProductFilter()
            }
        </div>
    </div>;
}

